import { useEffect } from 'react'

import { getPublishUrl } from '@buffer-mono/shared-utils/urls'

import { useUser } from '../../../../common/context/User'
import { isInPublish, redirectToUrl } from '../../../../common/utils/urls'

const FORCED_SETUP_PATH = '/setup-2fa-required'

/**
 * Redirects the user to the forced 2FA setup route on Publish whenever the
 * current organization reports `shouldEnforce2FASetup = true`. That field
 * is the server-derived gate — it already combines the per-org
 * `settings.enforce2FA` toggle and the actor's own 2FA state. Turning the
 * toggle off (or completing setup) clears it for the next request.
 *
 * Server-side `session-helpers.ts` is the authoritative gate; this
 * component is the matching UX so members hit a friendly setup screen
 * instead of a string of rejected requests.
 */
export function Enforce2FARedirect(): null {
  const user = useUser()

  const shouldEnforce2FASetup = Boolean(
    user.currentOrganization?.shouldEnforce2FASetup,
  )
  const isOnSetupPath = window.location.pathname.startsWith(FORCED_SETUP_PATH)

  useEffect(() => {
    if (!user.id) return
    if (!shouldEnforce2FASetup) return
    if (isOnSetupPath) return
    // The forced setup route lives in publish-frontend. If the user is on
    // another product (account, engage, etc.) we still bounce them to the
    // publish-hosted setup screen — the server gate rejects every request
    // from this user across the API until 2FA is set, so anything else
    // would just dead-end.
    redirectToUrl(
      isInPublish()
        ? `${window.location.origin}${FORCED_SETUP_PATH}`
        : getPublishUrl('setup-2fa-required'),
    )
  }, [
    user.id,
    user.currentOrganization?.id,
    shouldEnforce2FASetup,
    isOnSetupPath,
  ])

  return null
}
